import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Star, MessageSquare, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';

/**
 * Public review-request landing page (/r/:token) — no login.
 *
 * Shows BOTH options side by side, always: "Review us on Google" and
 * "Send us a private note." Never sequenced behind a sentiment
 * question — the FTC Consumer Reviews Rule (and Google policy) ban
 * routing unhappy patients away from the public review option.
 *
 * Loading the page stamps clicked_at; tapping the Google button stamps
 * google_clicked_at before redirecting, so the staff dashboard can
 * show sent → opened → tapped-through.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ReviewLinkPage() {
  const { token } = useParams();
  // A new magic link must never inherit another recipient's data or signature.
  return <ReviewLinkSession key={token} token={token} />;
}

function ReviewLinkSession({ token }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const sendLock = useRef(false);

  useEffect(() => {
    axios.get(`${API_BASE}/reviews/public/${token}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((err) => setState({
        loading: false, data: null,
        error: err.response?.data?.error || 'This link is no longer valid.',
      }));
  }, [token]);

  async function handleGoogle() {
    const url = state.data?.google_review_url;
    if (!url) return;
    try { await axios.post(`${API_BASE}/reviews/public/${token}/google-click`); } catch { /* never block */ }
    window.location.href = url;
  }

  async function handleSendNote() {
    if (!note.trim() || sendLock.current) return;
    sendLock.current = true;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/reviews/public/${token}/feedback`, { body: note.trim() });
      setSent(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not send your note. Please try again.');
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  }

  const shell = (children) => (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-900/[0.06]">
        {children}
      </div>
    </div>
  );

  if (state.loading) {
    return shell(<div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-gray-300" /></div>);
  }
  if (state.error) {
    return shell(
      <div className="text-center">
        <AlertTriangle className="mx-auto mb-3 h-9 w-9 text-amber-400" />
        <p className="text-[15px] text-gray-700">{state.error}</p>
      </div>,
    );
  }
  if (sent) {
    return shell(
      <div className="text-center">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
        <h1 className="text-[20px] font-bold text-gray-900">Thank you!</h1>
        <p className="mt-2 text-[14px] text-gray-600">
          Your note went straight to our team at {state.data.location_name}. We read every one.
        </p>
      </div>,
    );
  }

  return shell(
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
        <Star className="h-7 w-7 text-[#CC0000]" />
      </div>
      <h1 className="text-[20px] font-bold text-gray-900">How was your visit?</h1>
      <p className="mt-1 text-[14px] text-gray-500">{state.data.location_name}</p>

      <div className="mt-6 space-y-3">
        {state.data.google_review_url && (
          <button
            onClick={handleGoogle}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#CC0000] px-6 py-3.5 text-[15px] font-bold text-white shadow-sm transition-colors hover:bg-[#a80000]"
          >
            <Star className="h-4 w-4" /> Review us on Google
          </button>
        )}

        {!showNote ? (
          <button
            onClick={() => setShowNote(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-gray-200 px-6 py-3.5 text-[15px] font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            <MessageSquare className="h-4 w-4" /> Send us a private note
          </button>
        ) : (
          <div className="space-y-2 text-left">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              aria-label="Private note to the practice"
              maxLength={5000}
              disabled={sending}
              autoFocus
              placeholder="Tell us anything — what went well, what we can do better…"
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[14px] focus:border-[#CC0000] focus:outline-none"
            />
            <button
              onClick={handleSendNote}
              disabled={sending || !note.trim()}
              className="w-full rounded-xl bg-gray-900 px-6 py-3 text-[14px] font-bold text-white transition-colors hover:bg-gray-700 disabled:opacity-40"
            >
              {sending ? 'Sending…' : 'Send to our team'}
            </button>
          </div>
        )}
      </div>

      <p className="mt-6 text-[11px] text-gray-400">
        Both options go a long way. Thank you for trusting us with your care.
      </p>
    </div>,
  );
}
